import { useTranslation } from 'next-i18next';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useAuthentication } from '@hotelplan/components.common.auth';
import { Button } from '@hotelplan/components.common.buttons';
import { Icon } from '@hotelplan/components.common.icon';
import { Tooltip } from '@hotelplan/components.common.tooltip';
import {
  AuthChannelType,
  OrlFlightAlternative,
  TravelType,
} from '@hotelplan/graphql.types';
import { useDeviceType } from '@hotelplan/libs.context.device-type';
import { useObjectId } from '@hotelplan/libs.context.object-id';
import { useToggleState } from '@hotelplan/libs.hooks-react';
import { useSearchState } from '@hotelplan/libs.search-state';
import { formatDate, formatPrice, parseDateStr } from '@hotelplan/libs.utils';
import { mapOrlFilterValuesToPdpFilterCriteriaInput } from 'components/domain/filters/Filters.mappers';
import { mapSearchControlStateToPdpSearchCriteriaInput } from 'components/domain/orl/Orl.mappers';
import { IOrlState } from 'components/domain/orl/Orl.types';
import { getTravelersCount } from 'components/domain/orl/Orl.utils';
import { useORLSortingContext } from 'components/domain/orl/orlResults/ORLSorting';
import AlternativeFlightResultsSkeleton from 'components/domain/orl/skeletons/AlternativeFlightResults.skeleton';
import { OrlItemFragment } from 'graphql/fragments/ORLItem.generated';
import { useGetAlternativeFlightsForOrlOfferLazyQuery } from 'graphql/orl/GetAlternativeFlightsForORLOffer.generated';
import { mapDurationToWeeks } from 'libs';
import AlternativeFlightItem from './AlternativeFlightItem';
import {
  OrlOfferDescription,
  OrlOfferFlight,
  ModalCloseButton,
} from './OfferItem.styled';
import OfferPriceDescription, { TooltipWrapper } from './OfferPriceDescription';

const DATE_FORMAT = 'EEEEEE dd.MM.yyyy';

interface IOrlOffer {
  offer: OrlItemFragment;
  onCheckout: (
    alternativeFlight?: OrlFlightAlternative,
    flightIndex?: number
  ) => void;
}

const useAlternativeFlights = (offerId: string) => {
  const { sortingValue } = useORLSortingContext();
  const {
    state: { searchControl, filters },
  } = useSearchState<IOrlState>();

  const { id } = useObjectId();

  const [getAlternativeFlightsForOrlOffer, { data, loading }] =
    useGetAlternativeFlightsForOrlOfferLazyQuery();

  return {
    loading,
    data: (data?.orl.searchResult.flightAlternatives ||
      []) as OrlFlightAlternative[],
    getAlternativeFlights: () => {
      return getAlternativeFlightsForOrlOffer({
        variables: {
          offerId,
          searchControl:
            mapSearchControlStateToPdpSearchCriteriaInput(searchControl),
          filters: mapOrlFilterValuesToPdpFilterCriteriaInput(filters),
          id,
          sort: sortingValue,
        },
      });
    },
  };
};

function OfferItem({ offer, onCheckout }: IOrlOffer) {
  const [t, { language }] = useTranslation(['common', 'orl']);
  const { mobile } = useDeviceType();
  const [isFlightBottomOpen, setFlightBottomOpen] = useState(false);
  const {
    state: { searchControl },
  } = useSearchState<IOrlState>();
  const [showTooltip, open, close] = useToggleState(false);

  const { channelType } = useAuthentication();
  const isB2BAuthenticated = channelType === AuthChannelType.B2B;

  const {
    departureDate,
    returnDate,
    duration,
    outboundFlight,
    rooms,
    transferPrice,
    transferIncluded,
    pricePerPerson,
    totalPrice,
    id: offerId,
    provider,
    productCode,
  } = offer;

  const {
    data: alternativeFlights,
    getAlternativeFlights,
    loading,
  } = useAlternativeFlights(offerId);

  const hasAlternativeFlights =
    searchControl?.travelType === TravelType.Package;

  const formatOfferDate = useCallback(
    (date: string) => formatDate(parseDateStr(date), DATE_FORMAT, language),
    [language]
  );

  const travellersCount = useMemo(() => {
    return getTravelersCount(offer?.rooms);
  }, [offer?.rooms]);

  const tooltipRef = useRef<HTMLDivElement>(null);

  function formatDayOfWeek(date: string) {
    const newDate = formatOfferDate(date).split('');
    newDate.splice(2, 0, '.');
    return newDate;
  }

  return (
    <div className="offer-item">
      <OrlOfferDescription hasAlternativeFlights={hasAlternativeFlights}>
        <div className="date">
          <span className="value">{formatDayOfWeek(departureDate)}</span>
          {mobile ? <span className="delimiter">-</span> : null}
          <span className="value"> {formatDayOfWeek(returnDate)}</span>
          {mobile ? <div>, {mapDurationToWeeks(duration, t)}</div> : null}
          {isB2BAuthenticated && !mobile && (
            <div className="b2b-details">{provider}</div>
          )}
        </div>
        <div className="departure-city">
          {hasAlternativeFlights ? t('common:flight.from') : null}{' '}
          {outboundFlight?.departure.name}
          {!mobile ? (
            <>
              <div>{mapDurationToWeeks(duration, t)}</div>
              {transferPrice || transferIncluded ? (
                <div className="transfer">
                  <div className="icon-wrapper">
                    <Icon name="transfer" size="xs" />
                  </div>

                  <div className="text">
                    {transferIncluded ? t('orl:offer.transferIncluded') : null}

                    {transferPrice ? (
                      <>
                        {t('orl:offer.transfer')} {`${t('common:priceFrom')}`}{' '}
                        {`${formatPrice(transferPrice)} ${t(
                          'common:transfer.perPerson'
                        )}`}
                      </>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
          {isB2BAuthenticated && mobile && (
            <div className="b2b-details">{provider}</div>
          )}
          {isB2BAuthenticated && (
            <div className="b2b-details">{productCode}</div>
          )}
        </div>
        <div className="room">
          {rooms.map(room => {
            return (
              <div className="room-wrap" key={room.roomType.code}>
                <div className="text">
                  <div className="type">{room.roomType.description}</div>
                  <div>{room.boardType.description}</div>
                </div>
              </div>
            );
          })}
        </div>

        {!mobile && hasAlternativeFlights ? (
          <div>
            <button
              className="flight-button-open"
              onClick={() => {
                getAlternativeFlights();
                setFlightBottomOpen(!isFlightBottomOpen);
              }}
            >
              {isFlightBottomOpen
                ? t('orl:close.button')
                : t('orl:show.button')}
            </button>
          </div>
        ) : null}

        {mobile && (transferPrice || transferIncluded) ? (
          <div className="transfer">
            <div className="icon-wrapper">
              <Icon name="transfer" size="xs" />
            </div>

            <div className="text">
              {transferIncluded ? t('orl:offer.transferIncluded') : null}

              {transferPrice ? (
                <>
                  {t('orl:offer.transfer')} {`${t('common:priceFrom')}`}{' '}
                  {`${formatPrice(transferPrice)} ${t(
                    'common:transfer.perPerson'
                  )}`}
                </>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="bottom-card">
          {mobile ? (
            <div>
              <h4 className="traveller-title">{t('orl:travellers.title')}</h4>
              <div className="text">
                {t('common:adult.count', { count: travellersCount.adults })}
                {travellersCount.children > 0 &&
                  `, ${t('common:child.count', {
                    count: travellersCount.children,
                  })}`}
              </div>
            </div>
          ) : null}
          <div>
            {mobile ? (
              <p className="price-title">{t('pricePerPerson.title')}</p>
            ) : null}
            <TooltipWrapper onMouseEnter={open} onMouseLeave={close}>
              <Button
                className="button"
                icon={{
                  name: 'chevron-long-right',
                  size: 'xs',
                }}
                onClick={e => {
                  e.preventDefault();
                  onCheckout();
                }}
              >
                {mobile ? formatPrice(pricePerPerson) : formatPrice(totalPrice)}
              </Button>
              {showTooltip && !mobile ? (
                <Tooltip tooltipElRef={tooltipRef} withAutoPositioning>
                  <OfferPriceDescription pricePerPerson={pricePerPerson} />
                </Tooltip>
              ) : null}
            </TooltipWrapper>
          </div>
        </div>
      </OrlOfferDescription>
      {hasAlternativeFlights ? (
        <OrlOfferFlight>
          {mobile ? (
            <Button
              className={`flight-toggle-button ${
                isFlightBottomOpen ? 'open' : ''
              }`}
              onClick={() => {
                getAlternativeFlights();
                setFlightBottomOpen(!isFlightBottomOpen);
              }}
              icon={{
                name: 'flug',
                size: 'sm',
              }}
            >
              {!isFlightBottomOpen
                ? t('orl:alternativeFlightOpen.button')
                : t('orl:alternativeFlightClose.button')}
              <Icon name="chevron-long-right" size="sm" className="chevron" />
            </Button>
          ) : null}
          {isFlightBottomOpen ? (
            <div className="flight-bottom">
              {!mobile ? (
                <ModalCloseButton
                  onClick={() => setFlightBottomOpen(!isFlightBottomOpen)}
                >{`Close alternative flight section`}</ModalCloseButton>
              ) : null}
              {loading ? (
                <AlternativeFlightResultsSkeleton />
              ) : (
                <>
                  <div className="flight-item-header">
                    {!mobile ? (
                      <>
                        <div className="column desktop">
                          <h3 className="title">
                            {t('orl:flightTimes.title')}
                          </h3>
                          <div className="subtitle">
                            {t('orl:flight.subtitle')}
                          </div>
                        </div>
                        <div className="column"></div>
                      </>
                    ) : null}
                    <div className="column">
                      {t('common:offer.departureDate')}{' '}
                      <div className="date">
                        {formatDayOfWeek(departureDate)}
                      </div>
                    </div>
                    <div className="column">
                      {t('common:offer.returnDate')}{' '}
                      <div className="date">{formatDayOfWeek(returnDate)}</div>
                    </div>
                    <span className="column">
                      {mobile ? t('common:pricePerPerson.title') : null}
                    </span>
                  </div>
                  {alternativeFlights.map((flight, index) => (
                    <AlternativeFlightItem
                      flight={flight}
                      key={flight.offerId}
                      transferPrice={transferPrice}
                      transferIncluded={transferIncluded}
                      onCheckout={alternativeFlight => {
                        return onCheckout(alternativeFlight, index);
                      }}
                    />
                  ))}
                </>
              )}
            </div>
          ) : null}
        </OrlOfferFlight>
      ) : null}
    </div>
  );
}

export default React.memo(OfferItem);
